import jwt_decode from "jwt-decode";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSearchParams } from "react-router-dom";
import useStorage, { StorageType } from "../hooks/useStorage";
import AuthApi, { CurrentUserClaims } from "../lib/auth";
import { useSetOnBehalfOf } from "../recoil/admin/hooks";
import { ApiResponse } from "../types/api";
import { LoginResponse } from "../types/login";

export interface IAuth {
	token?: string;
	login: (phoneNumber: string, code: string) => Promise<ApiResponse<LoginResponse>>;
	sendOtp: (phoneNumber: string, action: string) => Promise<ApiResponse>;
	logout: () => Promise<void>;
	switchAccount: (accountId: string) => Promise<void>;
	currentUserClaims: CurrentUserClaims | undefined;
	isAuthenticated: boolean;
	dashboardRedirect: string | null;
	clearDashboardRedirect: () => void;
	loginWithToken: (token: string) => Promise<boolean>;
	isLoadingAutenticated: boolean;
}

const defaultState: IAuth | undefined = undefined;
export const AuthContext = createContext<IAuth | undefined>(defaultState);
AuthContext.displayName = "AuthContext";

const ONE_DAY = 86400000;
const ONE_MINUTE = 60000;

export const AuthContextProvider: React.FC<{ children: ReactNode | ReactNode[] }> = ({ children }) => {
	const queryClient = useQueryClient();
	const setOnBehalfOf = useSetOnBehalfOf();
	const [searchParams] = useSearchParams();

	const [token, setToken] = useStorage<string | undefined>("token", undefined);
	const [currentUserClaims, setCurrentUserClaims] = useState<CurrentUserClaims | undefined>();
	const [dashboardRedirect, setDashboardRedirect] = useStorage<string | null>(
		"DASHBOARD_REDIRECT",
		null,
		StorageType.SESSION
	);
	const authApi = new AuthApi(token ?? "");

	const {
		data: isAuthenticated,
		refetch: recheckIsAuthenticated,
		isLoading: isLoadingAutenticated,
	} = useQuery("isAuthenticated", () => validateToken(), {
		cacheTime: ONE_MINUTE,
		enabled: !!token,
		refetchOnWindowFocus: false,
	});
	const logoutMutation = useMutation(
		"logoutMutate",
		() => {
			return authApi.logout();
		},
		{
			onSuccess: () => {
				setToken("");
				queryClient.setQueryData("isAuthenticated", false);
			},
			onMutate: () => {
				setToken("");
				queryClient.setQueryData("isAuthenticated", false);
			},

			onSettled: () => {
				setToken("");
				queryClient.invalidateQueries("isAuthenticated");
			},

			onError: () => {
				setToken("");
				queryClient.setQueryData("isAuthenticated", false);
			},
		}
	);

	useQuery(
		"refresh-token",
		() => {
			// if there is less than 1 day left on the token, refresh it
			const needsToBeRefreshed = token && jwt_decode<{ exp: number }>(token).exp * 1000 - Date.now() < ONE_DAY;
			if (!needsToBeRefreshed) {
				return;
			}

			return authApi.getRefreshToken();
		},
		{
			enabled: !!token,
			cacheTime: ONE_DAY,
			refetchOnWindowFocus: false,
			onSuccess: (_refreshToken) => {
				if (!_refreshToken) {
					return;
				}
				setToken(_refreshToken);
			},
		}
	);

	async function switchAccount(accountId: string) {
		const response = await authApi.switchAccount(accountId);
		setToken(response.isSuccess ? response.result?.accessToken : "");
	}

	async function sendOtp(phoneNumber: string, action: string) {
		return await authApi.requestOtp(phoneNumber, action);
	}

	async function login(phoneNumber: string, otp: string) {
		const response = await authApi.login(phoneNumber, otp);

		if (response.isSuccess) {
			setToken(response.result?.accessToken);
		}

		return response;
	}

	async function loginWithToken(seamlessToken: string): Promise<boolean> {
		const api = new AuthApi(seamlessToken);

		const validateResponse = await api.validateToken();
		if (!validateResponse.isSuccess) return false;

		const longLivedToken = await api.getRefreshToken();
		if (!longLivedToken) return false;

		setToken(longLivedToken);

		setCurrentUserClaims(validateResponse.result);
		queryClient.setQueryData("isAuthenticated", true);
		return true;
	}

	async function logout() {
		setOnBehalfOf(undefined);
		await logoutMutation.mutateAsync();
	}

	async function validateToken() {
		if (!token) {
			setCurrentUserClaims(undefined);
			return false;
		}

		const tokenBeingValidated = token;
		const response = await authApi.validateToken();
		if (response.isSuccess) {
			setCurrentUserClaims(response.result);
			return true;
		}

		setToken((current) => {
			if (current !== tokenBeingValidated) return current;
			setCurrentUserClaims(undefined);
			setOnBehalfOf(undefined);
			return "";
		});
		return false;
	}

	useEffect(
		function validateTokenEffect() {
			if (token) {
				recheckIsAuthenticated();
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[token]
	);

	useEffect(() => {
		const redirectFromSearch = searchParams.get("dashboardRedirect");
		if (redirectFromSearch) {
			setDashboardRedirect(decodeURIComponent(redirectFromSearch));
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	function clearDashboardRedirect() {
		setDashboardRedirect(null);
	}

	const value: IAuth = {
		token: token ?? undefined,
		login,
		sendOtp,
		logout,
		switchAccount,
		currentUserClaims,
		isAuthenticated: isAuthenticated ?? false,
		dashboardRedirect,
		clearDashboardRedirect,
		loginWithToken,
		isLoadingAutenticated,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default function useAuth() {
	const context = useContext(AuthContext);

	if (context === undefined) {
		throw new Error("useAuth must be used within a AuthProvider");
	}

	return context;
}
